import { useEffect, useMemo, useState } from 'react'
import { StatCard } from '@/pages/Organization/Analytics/components/StatCard'
import { GaugeChart } from '@/pages/Organization/Analytics/components/GaugeChart'
import { DonutChart } from '@/pages/Organization/Analytics/components/DonutChart'
import { IPVPNavigation } from '@/components/Navbar'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { useAuth } from '@/context/AuthContext'
import { adminApi } from '@/utils/api'
import { useNavigate } from 'react-router-dom'
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import type {
  AdminDashboard as AdminDashboardType,
  AdminMetricsTrends,
  AdminRiskAnalysis,
  AdminCompletionRate,
} from '@/types/admin'

const mockKpis = [
  { label: 'Total Users', value: '24,310', trend: '+8.2%', muted: 'vs last 30d' },
  { label: 'Organizations', value: '142', trend: '+12%', muted: 'pending 6' },
  { label: 'Assessments Completed', value: '18,902', trend: '+5.4%', muted: 'last 30d' },
  { label: 'High-Risk Couples', value: '312', trend: '-2.1%', muted: 'flagged' },
]

export default function AdminDashboard() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [dashboard, setDashboard] = useState<AdminDashboardType | null>(null)
  const [trends, setTrends] = useState<AdminMetricsTrends>([])
  const [risk, setRisk] = useState<AdminRiskAnalysis | null>(null)
  const [completion, setCompletion] = useState<AdminCompletionRate[]>([])
  const [error, setError] = useState<string | null>(null)
  const [trendPeriod, setTrendPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('7d')

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        const [dashRes, trendsRes, riskRes, completionRes] = await Promise.all([
          adminApi.dashboard(),
          adminApi.metricsTrends({ period: trendPeriod }),
          adminApi.riskAnalysis(),
          adminApi.completionRates(),
        ])
        setDashboard((dashRes as { data?: AdminDashboardType }).data || (dashRes as AdminDashboardType))
        const trendsPayload = trendsRes as { data?: { data?: AdminMetricsTrends } }
        setTrends(trendsPayload.data?.data || (trendsRes as { data?: AdminMetricsTrends }).data || (trendsRes as unknown as AdminMetricsTrends))
        setRisk((riskRes as { data?: AdminRiskAnalysis }).data || (riskRes as unknown as AdminRiskAnalysis))
        setCompletion(
          (completionRes as { data?: AdminCompletionRate[] }).data || (completionRes as unknown as AdminCompletionRate[])
        )
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load admin dashboard')
      }
    }
    void load()
  }, [trendPeriod])

  const assessmentRisk = dashboard?.assessmentMetrics?.riskDistribution

  const donutData = useMemo(
    () => {
      const colors = ['#22C55E', '#F59E0B', '#EF4444']
      if (risk?.labels && risk.values) {
        const total = risk.values.reduce((sum, val) => sum + val, 0)
        return risk.labels.map((label, idx) => ({
          label,
          value: risk.values[idx] || 0,
          color: colors[idx] ?? '#94A3B8',
          percentage: total > 0 ? `${Math.round(((risk.values[idx] || 0) / total) * 100)}%` : '0%'
        }))
      }
      if (assessmentRisk) {
        const values = [
          assessmentRisk.low ?? 0,
          assessmentRisk.moderate ?? 0,
          assessmentRisk.high ?? 0,
        ]
        const total = values.reduce((sum, val) => sum + val, 0)
        return ['Low', 'Moderate', 'High'].map((label, idx) => ({
          label,
          value: values[idx] ?? 0,
          color: colors[idx] ?? '#94A3B8',
          percentage: total > 0 ? `${Math.round(((values[idx] ?? 0) / total) * 100)}%` : '0%'
        }))
      }
      return ['Low', 'Medium', 'High'].map((label, idx) => ({
        label,
        value: 0,
        color: colors[idx] ?? '#94A3B8',
        percentage: '0%'
      }))
    },
    [risk, assessmentRisk]
  )

  const barData = useMemo(
    () => {
      if (trends?.length) {
        const mapped = trends.map((t, idx) => ({
          label: t.date
            ? new Date(t.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            : `Day ${idx + 1}`,
          value: (t as { users?: number; value?: number }).users ?? t.value ?? 0,
        }))
        if (mapped.length <= 30) return mapped
        const step = Math.max(1, Math.ceil(mapped.length / 30))
        return mapped.filter((_, idx) => idx === mapped.length - 1 || idx % step === 0)
      }
      return [
        { label: 'Users', value: dashboard?.systemOverview?.totalUsers ?? 0 },
        { label: 'Organizations', value: dashboard?.systemOverview?.totalOrganizations ?? 0 },
        { label: 'Couples', value: dashboard?.systemOverview?.totalCouples ?? 0 },
        { label: 'Assessments', value: dashboard?.systemOverview?.totalAssessments ?? 0 },
      ]
    },
    [trends, dashboard?.systemOverview]
  )

  const kpis = useMemo(() => {
    const overview = dashboard?.systemOverview
    if (!overview) return mockKpis
    return [
      {
        label: 'Total Users',
        value: (overview.totalUsers ?? 0).toLocaleString(),
        trend: '',
        muted: 'All users',
      },
      {
        label: 'Organizations',
        value: (overview.totalOrganizations ?? 0).toLocaleString(),
        trend: '',
        muted: '',
      },
      {
        label: 'Assessments Completed',
        value: (overview.completedAssessments ?? 0).toLocaleString(),
        trend: '',
        muted: '',
      },
      {
        label: 'High-Risk Couples',
        value: (dashboard.assessmentMetrics?.riskDistribution?.high ?? 0).toLocaleString(),
        trend: '',
        muted: '',
      },
    ]
  }, [dashboard])

  const completionRateFallback = dashboard?.assessmentMetrics?.completionRate ?? 0
  const handleSignOut = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title='Admin Dashboard'
          infoText='System-wide overview across users, organizations, assessments, and learning.'
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
        {error && (
          <p className='mt-2 text-sm text-red-600 text-start'>{error}</p>
        )}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'>
        {kpis.map((kpi) => (
          <StatCard
            key={kpi.label}
            title={kpi.label}
            value={kpi.value}
            subtitle={kpi.muted}
            trend={kpi.trend}
          />
        ))}
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-3 gap-4'>
        <div className='col-span-2 bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-lg font-semibold text-gray-900'>Growth Trends</h3>
            <div className='flex items-center gap-2 text-sm'>
              <span className='text-gray-600'>Range</span>
              <select
                value={trendPeriod}
                onChange={(e) => setTrendPeriod(e.target.value as typeof trendPeriod)}
                className='border border-gray-200 rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500'
              >
                <option value='7d'>Last 7 days</option>
                <option value='30d'>Last 30 days</option>
                <option value='90d'>Last 90 days</option>
                <option value='1y'>Last year</option>
              </select>
            </div>
          </div>
          <div className='h-80 w-full'>
            <ResponsiveContainer width='100%' height='100%'>
              <ReBarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray='3 3' vertical={false} />
                <XAxis dataKey='label' tick={{ fontSize: 10 }} interval={0} height={50} angle={-25} textAnchor='end' />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value: number) => value}
                  labelFormatter={(label) => `Date: ${label}`}
                  cursor={{ fill: '#f8fafc' }}
                />
                <Bar dataKey='value' fill='#111827' radius={[4, 4, 0, 0]} maxBarSize={32} />
              </ReBarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Risk Distribution</h3>
          <DonutChart data={donutData} />
        </div>
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-3 gap-4'>
        <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm xl:col-span-2'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Completion Rates</h3>
          <GaugeChart
            value={
              completion.length
                ? Math.round(
                    completion.reduce((sum, item) => sum + (item.completionRate ?? 0), 0) / completion.length
                  )
                : Math.round(completionRateFallback)
            }
            label='Avg Completion'
          />
          <p className='text-sm text-gray-600 mt-4'>
            Compare completion rates across organizations to spot drop-offs and coach admins.
          </p>
        </div>
        <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>System Health</h3>
          <ul className='space-y-3 text-sm text-gray-700'>
            <li className='flex items-center justify-between'>
              <span>Database</span>
              <span className='text-green-600 font-semibold'>Healthy</span>
            </li>
            <li className='flex items-center justify-between'>
              <span>Storage</span>
              <span className='text-green-600 font-semibold'>Healthy</span>
            </li>
            <li className='flex items-center justify-between'>
              <span>WhatsApp</span>
              <span className='text-yellow-600 font-semibold'>Degraded</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
